import { useEffect, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { adminGetPost, adminUpdatePost, getProfile, isApiConfigured } from './api';
import { PostStatus } from '../types/content';

export interface AuthorControls {
  /** Any admin may edit, publish and unpublish. */
  canEdit: boolean;
  /** Only the author may delete — matches the server rule. */
  canDelete: boolean;
  status: PostStatus | null;
  busy: boolean;
  error: string | null;
  setStatus: (next: PostStatus) => Promise<void>;
}

const NOTHING: AuthorControls = {
  canEdit: false,
  canDelete: false,
  status: null,
  busy: false,
  error: null,
  setStatus: async () => {},
};

/**
 * Decides whether to offer inline editing controls on a public article page.
 *
 * Anonymous readers never trigger a request — `getCurrentUser()` fails fast and
 * the hook short-circuits, so the reading path stays exactly as cheap as it was.
 * Only a signed-in admin pays for the extra lookup that reveals `authorSub`,
 * which the public post payload deliberately omits.
 */
export function useAuthorControls(postId: string | null): AuthorControls {
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [status, setStatusState] = useState<PostStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setCanEdit(false);
    setCanDelete(false);
    setStatusState(null);

    if (!postId || !isApiConfigured) return;

    (async () => {
      try {
        await getCurrentUser();
      } catch {
        return; // Signed out — the common case, and it costs one local check.
      }

      try {
        const me = await getProfile();
        if (cancelled || !me.isAdmin) return;

        const post = await adminGetPost(postId);
        if (cancelled) return;

        setCanEdit(true);
        setCanDelete(Boolean(post.authorSub) && post.authorSub === me.sub);
        setStatusState(post.status);
      } catch {
        /* Not entitled, or the post is gone — show no controls. */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postId]);

  const setStatus = async (next: PostStatus) => {
    if (!postId) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await adminUpdatePost(postId, { status: next });
      setStatusState(updated.status);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change the status.');
    } finally {
      setBusy(false);
    }
  };

  if (!postId) return NOTHING;

  return { canEdit, canDelete, status, busy, error, setStatus };
}
